import { ref } from 'vue'
import getSensorData from '../../../../composables/getSensorData'

const processKneeBraceData = () => {
  const kneeBraceData = ref(new Map())
  const shouldRenderKneeBraceCharts = ref(false)
  const kneeBraceSensorData = ref([])
  const downsampleFactor = 1 // every 10th data point is taken.
  const downsampleMethod = ''
  const maxDataPointsAllowed = 5000
  const timeDataFetched = ref(false)
  const sensorTypesUpdated = ref(false)
  const kneeSleeveSensorTypes = ref([
    'Angle', 'Hamstring', 'Calf', 'Quad', 'Shin'
  ])
  const timeData = ref([])

  const { fetchTimeData, fetchDataForChart, fetchSensorLabels } =
    getSensorData()

  const clearKneeSleeveSessionData = () => {
    timeData.value = []
    kneeBraceData.value.clear()
    timeDataFetched.value = false
    sensorTypesUpdated.value = false
    kneeSleeveSensorTypes.value = [
      'Angle', 'Hamstring', 'Calf', 'Quad', 'Shin'
    ]
  }

  const renderSingleTypeSensorData = async (sessionId, userDevices, sensorType) => {
    if (!timeDataFetched.value) {
      timeData.value = await fetchTimeData(sessionId)
      timeDataFetched.value = true
    }
    if (sensorType.includes('Group')) {
      await renderGroupData(sessionId, userDevices, sensorType)
      return
    }

    if (sensorType.includes('Sensors')) {
      await renderSensorData(sessionId, userDevices, sensorType)
      return
    }
    const res = await fetchDataForChart(sessionId, sensorType, userDevices, timeData.value)
    // console.log('res:', res)

    if (!res.length) {
      shouldRenderKneeBraceCharts.value = true
      return
    }

    if (!sensorTypesUpdated.value) {
      const sides = res.map(item => item.name).sort((a, b) => {
        if (a === 'Left' && b === 'Right') return -1
        if (a === 'Right' && b === 'Left') return 1
        return 0
      })

      for (const side of sides) {
        kneeSleeveSensorTypes.value.push(`${side} Group`)
      }

      for (const side of sides) {
        kneeSleeveSensorTypes.value.push(`${side} Sensors`)
      }

      sensorTypesUpdated.value = true
    }

    const processedData = processAndDownsampleData(res, downsampleFactor, downsampleMethod)
    kneeBraceData.value.set(`knee-${sensorType.toLowerCase()}`, {
      title: `${sensorType}`,
      data: processedData
    })

    shouldRenderKneeBraceCharts.value = true
  }

  const renderGroupData = async (sessionId, userDevices, sensorType) => {
    const groupData = []
    const side = sensorType.split(' ')[0]

    if (kneeBraceData.value.has(`knee-${side.toLowerCase()} group`)) {
      return
    }

    // Check if all sensor types have been fetched; if not, fetch them.
    const fetchPromises = []
    for (const sensorType of kneeSleeveSensorTypes.value) {
      if (!sensorType.includes('Group') && !sensorType.includes('Sensors')) {
        const key = `knee-${sensorType.toLowerCase()}`
        if (!kneeBraceData.value.has(key)) {
          fetchPromises.push(renderSingleTypeSensorData(sessionId, userDevices, sensorType))
        }
      }
    }
    await Promise.all(fetchPromises)

    kneeBraceData.value.forEach((data, key) => {
      if (data.data.length > 0) {
        for (const dataPoint of data.data) {
          if (dataPoint.name === side && !key.includes('group')) {
            groupData.push({
              name: data.title,
              data: dataPoint.data
            })
          }
        }
      }
    })

    kneeBraceData.value.set(`knee-${side.toLowerCase()} group`, {
      title: sensorType,
      data: groupData
    })
  }

  const renderSensorData = async (sessionId, userDevices, sensorType) => {
    const side = sensorType.split(' ')[0]
    const mapKeyLeft = 'knee-left sensors'
    const mapKeyRight = 'knee-right sensors'

    // If both left and right data are already fetched, return
    if (kneeBraceData.value.has(mapKeyLeft) && kneeBraceData.value.has(mapKeyRight)) {
      return
    }

    const sensorIdToLabel = await fetchSensorLabels('kneeBrace')
    const sensorsToPlot = Object.keys(sensorIdToLabel)
    const fetchPromises = []

    sensorsToPlot.forEach((id) => {
      fetchPromises.push(fetchDataForChart(sessionId, `${id}`, userDevices, timeData.value))
    })

    try {
      const results = await Promise.all(fetchPromises)
      const aggregatedDataLeft = []
      const aggregatedDataRight = []
      const percentageChangeSeriesLeft = []
      const percentageChangeSeriesRight = []

      for (let i = 0; i < sensorsToPlot.length; i++) {
        const sensorLabel = sensorIdToLabel[sensorsToPlot[i]]

        results[i].forEach((resData) => {
          const downsampledData = downsample(resData, downsampleFactor, downsampleMethod)

          const data = downsampledData.data
          const percentageChangeData = []

          if (data.length) {
            for (let i = 0; i < data.length; i++) {
              const change = ((data[i][1] / data[0][1]) - 1) * 100
              percentageChangeData.push([data[i][0], change])
            }
          }
          if (resData.name === 'Left') {
            aggregatedDataLeft.push({
              ...downsampledData,
              name: sensorLabel,
              alternativeName: sensorsToPlot[i]
            })
            percentageChangeSeriesLeft.push({
              name: sensorLabel,
              data: percentageChangeData,
              alternativeName: sensorsToPlot[i]
            })
          } else if (resData.name === 'Right') {
            aggregatedDataRight.push({
              ...downsampledData,
              name: sensorLabel,
              alternativeName: sensorsToPlot[i]
            })
            percentageChangeSeriesRight.push({
              name: sensorLabel,
              data: percentageChangeData,
              alternativeName: sensorsToPlot[i]
            })
          }
        })
      }

      //  cache the data for both sides.
      if ((side === 'Left' || side === 'Right')) {
        if (aggregatedDataLeft.length > 0) {
          kneeBraceData.value.set(mapKeyLeft, {
            title: 'Left Sensors',
            data: aggregatedDataLeft
          })

          kneeBraceData.value.set(`${mapKeyLeft}-percentageChangeData`, {
            title: `${side} Sensors Percentage Change`,
            data: percentageChangeSeriesLeft
          })
        }
        if (aggregatedDataRight.length > 0) {
          kneeBraceData.value.set(mapKeyRight, {
            title: 'Right Sensors',
            data: aggregatedDataRight
          })

          kneeBraceData.value.set(`${mapKeyRight}-percentageChangeData`, {
            title: `${side} Sensors Percentage Change`,
            data: percentageChangeSeriesRight
          })
        }
      }
    } catch (err) {
      console.log('Error:', err)
    }
  }

  function processAndDownsampleData (resData, factor, method) {
    return resData.map(dataItem => ({
      name: dataItem.name,
      data: downsample(dataItem, factor, method).data
    }))
  }

  function downsample (dataItem, factor, method) {
    const originalData = dataItem.data
    if (originalData.length < maxDataPointsAllowed || factor === 1) {
      return dataItem
    }
    const downsampledData = []
    const downsampledTimestamps = []

    // Separate timestamps and data values
    const timestamps = originalData.map(item => item[0])
    const values = originalData.map(item => item[1])

    // Downsample the values
    for (let i = 0; i < values.length; i += factor) {
      if (method === 'average') {
        let sum = 0
        for (let j = 0; j < factor && i + j < values.length; j++) {
          sum += values[i + j]
        }
        downsampledData.push(sum / factor)
      } else if (method === 'max') {
        let max = Number.MIN_SAFE_INTEGER
        for (let j = 0; j < factor && i + j < values.length; j++) {
          max = Math.max(max, values[i + j])
        }
        downsampledData.push(max)
      } else if (method === 'min') {
        let min = Number.MAX_SAFE_INTEGER
        for (let j = 0; j < factor && i + j < values.length; j++) {
          min = Math.min(min, values[i + j])
        }
        downsampledData.push(min)
      } else { // Simple sampling
        downsampledData.push(values[i])
      }

      // For timestamps, sample the first timestamp in each block
      downsampledTimestamps.push(timestamps[i])
    }

    // Combine timestamps and downsampled data
    const combinedData = downsampledTimestamps.map((timestamp, index) => [timestamp, downsampledData[index]])
    return {
      name: dataItem.name,
      data: combinedData
    }
  }

  return {
    kneeBraceData,
    shouldRenderKneeBraceCharts,
    kneeBraceSensorData,
    renderSingleTypeSensorData,
    clearKneeSleeveSessionData,
    kneeSleeveSensorTypes
  }
}
export default processKneeBraceData
